/**
 * Hint — the one-element convenience face of the global tooltip system.
 *
 * Wraps a trigger with <Tooltip>/<TooltipTrigger asChild>/<TooltipContent>
 * so a call site converts a browser-default `title="…"` into a suite
 * tooltip by wrapping the element and renaming the prop. All timing,
 * positioning and appearance defaults come from the central config
 * (`tooltipConfig.ts` + suite-ui.css tokens); pass `side`/`sideOffset`
 * only when a specific surface needs to deviate.
 */

import React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import type { TooltipSide } from './tooltipConfig';

export interface HintProps {
    /** Tooltip text; dynamic values (e.g. i18n strings) work as-is */
    label: React.ReactNode;
    /** Optional keyboard-shortcut hint rendered beside the label */
    shortcut?: React.ReactNode;
    /** The single trigger element (button, span, icon, …) rendered as-is */
    children?: React.ReactNode;
    /** Override the central default side */
    side?: TooltipSide;
    /** Override the central default trigger→content gap */
    sideOffset?: number;
    /** Extra classes on the tooltip surface, layered over `suite-ui-tooltip` */
    className?: string;
    /** Disables this hint only (the central config's `disabled` silences all) */
    disabled?: boolean;
    /** Same gate as <Tooltip shouldOpen> */
    shouldOpen?: (trigger: HTMLElement | null) => boolean;
}

export const Hint: React.FC<HintProps> = ({
    label,
    shortcut,
    children,
    side,
    sideOffset,
    className,
    disabled = false,
    shouldOpen,
}) => {
    // An empty label (e.g. a conditional title that is undefined right now)
    // must behave like the native attribute: no tooltip at all.
    const hasLabel =
        label !== undefined &&
        label !== null &&
        !(typeof label === 'string' && label.trim() === '');
    if (disabled || !hasLabel) return <>{children}</>;
    return (
        <Tooltip shouldOpen={shouldOpen}>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent side={side} sideOffset={sideOffset} className={className}>
                {label}
                {shortcut !== undefined && shortcut !== null && (
                    <span className="suite-ui-tooltip-shortcut">{shortcut}</span>
                )}
            </TooltipContent>
        </Tooltip>
    );
};
